import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { useOfflineStore } from '../stores/offlineStore';
import { cacheResponse, getCachedResponse } from '../services/apiCache';
import { captureInspectionDraftSnapshot } from './inspectionEmergencyDraft';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000, // 15s - fail fast instead of hanging (e.g. when emulator can't reach API)
  withCredentials: true // Send HttpOnly cookies
});

/** Single-flight refresh so parallel 401s (e.g. AuthImage) don't rotate/revoke the same cookie. */
let refreshPromise = null;

function refreshAccessTokenSingleFlight() {
  if (!refreshPromise) {
    refreshPromise = useAuthStore
      .getState()
      .refreshAccessToken()
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Response interceptor - Cache GET responses & handle token refresh and offline detection
api.interceptors.response.use(
  (response) => {
    const responseType = response.config?.responseType;
    const contentType = response.headers?.['content-type'] || '';

    if (
      responseType === 'blob' ||
      responseType === 'arraybuffer' ||
      contentType.includes('application/pdf')
    ) {
      return response;
    }

    // Cache successful GET responses in IndexedDB for offline use
    const method = (response.config?.method || '').toLowerCase();
    if (method === 'get' && response.status === 200) {
      const url = response.config?.url || '';
      // Don't cache auth endpoints or file downloads
      if (!url.includes('/auth/') && !url.includes('/download')) {
        const fullUrl = `${response.config?.baseURL || ''}${url}`;
        cacheResponse(fullUrl, response.data).catch(() => {});
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = (originalRequest?.url || '').toString();
    const isLoginRequest = requestUrl.includes('auth/login');
    const isVerifyLoginOtp = requestUrl.includes('auth/verify-login-otp');

    // Detect offline / network errors
    const isNetworkError =
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      !error.response;

    if (isNetworkError) {
      // Mark the error so callers can distinguish offline from server errors
      error.isOffline = true;

      if (!navigator.onLine) {
        useOfflineStore.getState().setOffline();
      }

      // For GET requests, try to serve from cache when offline
      const method = (originalRequest?.method || '').toLowerCase();
      if (method === 'get') {
        const fullUrl = `${originalRequest?.baseURL || ''}${requestUrl}`;
        const cached = await getCachedResponse(fullUrl, true); // ignoreExpiry when offline
        if (cached) {
          console.log('[api] Serving cached response for:', requestUrl);
          return { data: cached, status: 200, config: originalRequest, _fromCache: true };
        }
      }

      return Promise.reject(error);
    }

    // Avoid forced logout while offline — allow cached GET responses for yard workflows
    if (
      !navigator.onLine &&
      (originalRequest?.method || '').toLowerCase() === 'get'
    ) {
      const fullUrl = `${originalRequest?.baseURL || ''}${requestUrl}`;
      const cached = await getCachedResponse(fullUrl, true);
      if (cached) {
        console.log('[api] Serving cached response (offline, non-network error) for:', requestUrl);
        return { data: cached, status: 200, config: originalRequest, _fromCache: true };
      }
      error.isOffline = true;
      return Promise.reject(error);
    }

    const isRefreshRequest = requestUrl.includes('auth/refresh');
    if (
      !isLoginRequest &&
      !isVerifyLoginOtp &&
      !isRefreshRequest &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        await refreshAccessTokenSingleFlight(); // Backend sets new cookies; no token to store
        return api(originalRequest); // Cookies sent automatically
      } catch (refreshError) {
        // Snapshot in-progress inspection work before clearing session (user-scoped local only)
        try {
          await captureInspectionDraftSnapshot();
        } catch {
          /* ignore */
        }
        const { logout } = useAuthStore.getState();
        // Keep emergency draft; still clear sync queue / caches to avoid cross-user leakage
        await logout({ clearEmergencyDrafts: false });
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

